import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Delete as DeleteIcon,
  Add as AddIcon,
  CloudUpload
} from '@mui/icons-material';
import { format } from 'date-fns';
import { vendorService } from '../services/vendorService';
import { attachmentService } from '../services/attachmentService';
import {
  MAX_ATTACHMENT_FILE_SIZE_BYTES,
  MAX_ATTACHMENT_FILE_SIZE_LABEL,
} from '../constants/attachmentLimits';
import AttachmentCategoryChips from '../components/AttachmentCategoryChips';
import {
  ProcurementSummaryBar,
  ProcurementSummaryField,
  ProcurementSummaryNotesDense
} from '../components/procurement/ProcurementSummaryBar';
import { fetchPaymentTermsOptions } from '../services/paymentTermsService';
import { DEFAULT_PAYMENT_TERMS } from '../constants/defaultPaymentTerms';

const emptyVendorForm = () => ({
  name: '',
  contact_name: '',
  email: '',
  phone: '',
  fax: '',
  website: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip_code: '',
  country: 'USA',
  tax_id: '',
  terms: '',
  notes: '',
  is_active: true
});

const mapVendorToForm = (v) => ({
  name: v.name ?? '',
  contact_name: v.contact_name ?? '',
  email: v.email ?? '',
  phone: v.phone ?? '',
  fax: v.fax ?? '',
  website: v.website ?? '',
  address_line1: v.address_line1 ?? '',
  address_line2: v.address_line2 ?? '',
  city: v.city ?? '',
  state: v.state ?? '',
  zip_code: v.zip_code ?? '',
  country: v.country ?? 'USA',
  tax_id: v.tax_id ?? '',
  terms: v.terms ?? '',
  notes: v.notes ?? '',
  is_active: v.is_active !== false
});

const ProcurementVendorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const initialNumericId = id !== 'new' ? parseInt(id, 10) : NaN;
  const [loading, setLoading] = useState(id !== 'new' && !Number.isNaN(initialNumericId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [vendor, setVendor] = useState(null);
  const [vendorForm, setVendorForm] = useState(emptyVendorForm());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [isDraggingOverAttachment, setIsDraggingOverAttachment] = useState(false);
  const [paymentTermsOptions, setPaymentTermsOptions] = useState(DEFAULT_PAYMENT_TERMS);

  const vendorId = isNew ? null : parseInt(id, 10);
  const invalidId = !isNew && Number.isNaN(vendorId);

  useEffect(() => {
    let cancelled = false;
    fetchPaymentTermsOptions().then((opts) => {
      if (cancelled) return;
      setPaymentTermsOptions(Array.isArray(opts) && opts.length ? opts : DEFAULT_PAYMENT_TERMS);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadVendor = useCallback(async () => {
    if (isNew || invalidId) return;
    try {
      setLoading(true);
      setError('');
      const res = await vendorService.getById(vendorId);
      const data = res?.data;
      if (!data) {
        setVendor(null);
        setError('Vendor not found');
        return;
      }
      setVendor(data);
      setVendorForm(mapVendorToForm(data));
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to load vendor');
    } finally {
      setLoading(false);
    }
  }, [isNew, invalidId, vendorId]);

  useEffect(() => {
    if (isNew) {
      setVendor(null);
      setVendorForm(emptyVendorForm());
      setLoading(false);
      setError('');
      return;
    }
    if (invalidId) {
      setVendor(null);
      setLoading(false);
      setError('Invalid vendor ID');
      return;
    }
    setLoading(true);
    loadVendor();
  }, [isNew, invalidId, loadVendor]);

  const loadAttachments = useCallback(async () => {
    if (!vendorId || Number.isNaN(vendorId)) {
      setAttachments([]);
      return;
    }
    try {
      setAttachmentsLoading(true);
      setAttachmentsError('');
      const res = await attachmentService.getForVendor(vendorId);
      const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      setAttachments(list);
    } catch (err) {
      console.error(err);
      setAttachmentsError(err.response?.data?.message || 'Failed to load attachments');
      setAttachments([]);
    } finally {
      setAttachmentsLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const handleInputChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setVendorForm((f) => ({ ...f, [field]: value }));
  };

  const handleSave = async () => {
    if (!vendorForm.name?.trim()) {
      setError('Vendor name is required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      if (isNew) {
        const res = await vendorService.create(vendorForm);
        const created = res?.data;
        if (created?.id) {
          navigate(`/procurement/vendors/${created.id}`, { replace: true });
        } else {
          navigate('/procurement/vendors');
        }
      } else {
        const res = await vendorService.update(vendorId, vendorForm);
        const data = res?.data;
        if (data) {
          setVendor(data);
          setVendorForm(mapVendorToForm(data));
        }
        await loadAttachments();
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!vendorId || Number.isNaN(vendorId)) return;
    try {
      setSaving(true);
      setError('');
      await vendorService.delete(vendorId);
      navigate('/procurement/vendors');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to delete vendor');
    } finally {
      setSaving(false);
      setDeleteConfirmOpen(false);
    }
  };

  const uploadAttachments = async (files) => {
    if (!files?.length || !vendorId || Number.isNaN(vendorId)) return;
    if (files.length > 5) {
      setAttachmentsError('You can upload a maximum of 5 files at a time.');
      return;
    }
    const tooLarge = files.filter((f) => f.size > MAX_ATTACHMENT_FILE_SIZE_BYTES);
    if (tooLarge.length > 0) {
      setAttachmentsError(`One or more files exceed the ${MAX_ATTACHMENT_FILE_SIZE_LABEL} size limit.`);
      return;
    }
    try {
      setAttachmentsLoading(true);
      setAttachmentsError('');
      for (const file of files) {
        await attachmentService.upload({ file, vendorId });
      }
      await loadAttachments();
    } catch (err) {
      console.error(err);
      setAttachmentsError(err.response?.data?.message || 'Failed to upload attachment');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleAttachmentFileInputChange = async (event) => {
    const fileList = event.target.files;
    if (!fileList?.length) return;
    await uploadAttachments(Array.from(fileList));
    event.target.value = '';
  };

  const handleAttachmentDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOverAttachment(false);
    const fileList = event.dataTransfer?.files;
    if (!fileList?.length) return;
    await uploadAttachments(Array.from(fileList));
  };

  const handleAttachmentDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOverAttachment(true);
  };

  const handleAttachmentDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOverAttachment(false);
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      setAttachmentsError('');
      await attachmentService.delete(attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) {
      console.error(err);
      setAttachmentsError(err.response?.data?.message || 'Failed to delete attachment');
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      if (!attachment?.id) return;
      setAttachmentsError('');
      const res = await attachmentService.download(attachment.id);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name || `attachment-${attachment.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setAttachmentsError(err.response?.data?.message || 'Failed to download attachment');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isNew && !loading && (invalidId || (error && !vendor))) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/procurement/vendors')} size="small" sx={{ mb: 1 }}>
          Back to Vendors
        </Button>
        <Alert severity="error">{error || 'Vendor not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/procurement/vendors')} size="small" sx={{ mb: 0.5 }}>
            Back
          </Button>
          <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
            {isNew ? 'New Vendor' : `Vendor: ${vendor?.name || vendorForm.name || '—'}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isNew && (
            <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => setDeleteConfirmOpen(true)} disabled={saving}>
              Delete
            </Button>
          )}
          <Button variant="contained" size="small" startIcon={<Save />} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!isNew && vendor && (
        <ProcurementSummaryBar secondaryRow={<ProcurementSummaryNotesDense text={vendorForm.notes} />}>
          <ProcurementSummaryField label="Vendor #">
            <Box sx={{ fontWeight: 600 }}>{vendor.vendor_number ?? vendor.id ?? '—'}</Box>
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Name">{vendorForm.name || '—'}</ProcurementSummaryField>
          <ProcurementSummaryField label="Contact">{vendorForm.contact_name || '—'}</ProcurementSummaryField>
          <ProcurementSummaryField label="Phone">{vendorForm.phone || '—'}</ProcurementSummaryField>
          <ProcurementSummaryField label="Email">{vendorForm.email || '—'}</ProcurementSummaryField>
          <ProcurementSummaryField label="City">{vendorForm.city || '—'}</ProcurementSummaryField>
          <ProcurementSummaryField label="Status">
            <Chip label={vendorForm.is_active !== false ? 'Active' : 'Inactive'} color={vendorForm.is_active !== false ? 'success' : 'default'} size="small" />
          </ProcurementSummaryField>
        </ProcurementSummaryBar>
      )}

      <Paper sx={{ p: 2, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}>
          {isNew ? 'Vendor details' : 'Edit vendor'}
        </Typography>
        <VendorFormFields
          formData={vendorForm}
          onChange={handleInputChange}
          showActive
          paymentTermsOptions={paymentTermsOptions}
        />
      </Paper>

      {!isNew && vendorId && (
        <Card sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
              Attachments
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Vendor documents (W-9, contracts, etc.). Up to 5 files at a time, {MAX_ATTACHMENT_FILE_SIZE_LABEL} each.
            </Typography>
            <Box
              sx={{
                mb: 1.5,
                minHeight: 120,
                p: 2,
                border: '2px dashed',
                borderColor: isDraggingOverAttachment ? 'primary.main' : 'grey.400',
                borderRadius: 2,
                bgcolor: isDraggingOverAttachment ? 'action.hover' : 'grey.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                transition: 'border-color 0.15s ease, background-color 0.15s ease'
              }}
              onDragOver={handleAttachmentDragOver}
              onDragLeave={handleAttachmentDragLeave}
              onDrop={handleAttachmentDrop}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CloudUpload sx={{ fontSize: 40, opacity: isDraggingOverAttachment ? 1 : 0.6 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Drop files here
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or use Upload · max 5 files, {MAX_ATTACHMENT_FILE_SIZE_LABEL} each
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Attachments: {attachments.length}
                  </Typography>
                </Box>
              </Box>
              <Button variant="contained" component="label" size="small" startIcon={<AddIcon />}>
                Upload
                <input type="file" hidden multiple onChange={handleAttachmentFileInputChange} />
              </Button>
            </Box>
            {attachmentsError && (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setAttachmentsError('')}>
                {attachmentsError}
              </Alert>
            )}
            {attachmentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : attachments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No attachments yet. Drag and drop files here or use Upload.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>File Name</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Uploaded At</TableCell>
                      <TableCell>Categories</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attachments.map((att) => (
                      <TableRow key={att.id} hover>
                        <TableCell>
                          <Button
                            size="small"
                            sx={{ textTransform: 'none' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadAttachment(att);
                            }}
                          >
                            {att.file_name}
                          </Button>
                        </TableCell>
                        <TableCell>
                          {att.file_size != null ? `${(att.file_size / 1024 / 1024).toFixed(2)} MB` : '—'}
                        </TableCell>
                        <TableCell>
                          {att.uploaded_at ? format(new Date(att.uploaded_at), 'MM/dd/yyyy HH:mm') : '—'}
                        </TableCell>
                        <TableCell>
                          <AttachmentCategoryChips
                            attachmentId={att.id}
                            categories={att.categories}
                            onUpdated={(row) =>
                              setAttachments((prev) =>
                                prev.map((a) => (a.id === row.id ? { ...a, ...row } : a))
                              )
                            }
                            onError={(msg) => setAttachmentsError(msg)}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => handleDeleteAttachment(att.id)} aria-label="Delete attachment">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={deleteConfirmOpen} onClose={() => !saving && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Vendor?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete vendor <strong>{vendorForm.name}</strong>? This cannot be undone. Vendors referenced by purchase orders cannot be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={saving}>
            {saving ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function VendorFormFields({ formData, onChange, showActive, paymentTermsOptions }) {
  return (
    <Grid container spacing={1.5}>
      <Grid item xs={12}>
        <TextField fullWidth size="small" label="Vendor Name" value={formData.name} onChange={onChange('name')} required />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Contact Name" value={formData.contact_name} onChange={onChange('contact_name')} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Email" type="email" value={formData.email} onChange={onChange('email')} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Phone" value={formData.phone} onChange={onChange('phone')} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Fax" value={formData.fax} onChange={onChange('fax')} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth size="small" label="Website" value={formData.website} onChange={onChange('website')} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth size="small" label="Address Line 1" value={formData.address_line1} onChange={onChange('address_line1')} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth size="small" label="Address Line 2" value={formData.address_line2} onChange={onChange('address_line2')} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" label="City" value={formData.city} onChange={onChange('city')} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" label="State" value={formData.state} onChange={onChange('state')} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" label="Zip Code" value={formData.zip_code} onChange={onChange('zip_code')} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" label="Country" value={formData.country} onChange={onChange('country')} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth size="small" label="Tax ID" value={formData.tax_id} onChange={onChange('tax_id')} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          select
          label="Payment Terms"
          value={formData.terms || ''}
          onChange={onChange('terms')}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {(Array.isArray(paymentTermsOptions) && paymentTermsOptions.length ? paymentTermsOptions : DEFAULT_PAYMENT_TERMS).map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth size="small" label="Notes" multiline rows={2} value={formData.notes} onChange={onChange('notes')} />
      </Grid>
      {showActive && (
        <Grid item xs={12}>
          <FormControlLabel
            control={<Checkbox checked={formData.is_active} onChange={(e) => onChange('is_active')(e)} size="small" />}
            label="Active"
          />
        </Grid>
      )}
    </Grid>
  );
}

export default ProcurementVendorDetail;
